/**
 * Runtime configuration accessor.
 *
 * This module provides a centralized way to access configuration values,
 * supporting both runtime injection (for Docker deployments) and build-time
 * environment variables (for local development).
 *
 * Configuration priority:
 * 1. Runtime config (window.APP_CONFIG) - injected by docker-entrypoint.sh at container startup
 * 2. Build-time env (import.meta.env) - baked into bundle by Vite
 * 3. Default fallback values
 *
 * This allows the same Docker image to work in any environment (staging, production, etc.)
 * by simply setting environment variables when starting the container.
 */

// Type for runtime config keys
type RuntimeConfigKey = 'API_URL' | 'GOOGLE_CLIENT_ID' | 'PLAID_ENV' | 'ENVIRONMENT' | 'POSTHOG_KEY' | 'POSTHOG_HOST';

/**
 * Vite's import.meta.env replacement map - captured to prevent static analysis.
 * By storing the env in an intermediate variable, we prevent Vite from
 * replacing individual property accesses with their build-time values.
 */
const viteEnv = import.meta.env;

/**
 * Get runtime config value or build-time fallback.
 * Using a function prevents Vite from statically analyzing and inlining values,
 * which is necessary for runtime configuration via window.APP_CONFIG.
 */
function getRuntimeConfig(key: RuntimeConfigKey, envKey: string, defaultValue: string): string {
  // Check runtime config first (Docker deployments)
  const runtimeValue = window.APP_CONFIG?.[key];
  if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
    return runtimeValue;
  }
  // Fall back to build-time value (local development)
  // Access via dynamic property to prevent Vite from inlining empty strings
  const buildTimeValue = viteEnv[envKey as keyof ImportMetaEnv] as string | undefined;
  if (buildTimeValue !== undefined && buildTimeValue !== null && buildTimeValue !== '') {
    return buildTimeValue;
  }
  return defaultValue;
}

/**
 * Application configuration object.
 * Values are resolved at module load time from runtime or build-time config.
 */
export const config = {
  /**
   * Base URL for API requests (e.g., https://api.flowio.me/v1)
   */
  get apiUrl(): string {
    return getRuntimeConfig('API_URL', 'VITE_API_URL', 'https://api.staging.flowio.me/v1');
  },

  /**
   * Google OAuth Client ID for social login
   * Returns the configured client ID or empty string if not configured.
   *
   * Priority:
   * 1. window.APP_CONFIG.GOOGLE_CLIENT_ID (runtime, Docker deployments)
   * 2. VITE_GOOGLE_CLIENT_ID (build-time, local development)
   * 3. '' (not configured)
   */
  get googleClientId(): string {
    // Check runtime config first (Docker deployments)
    const runtimeValue = window.APP_CONFIG?.GOOGLE_CLIENT_ID;
    if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
      return runtimeValue;
    }
    // Fall back to build-time value (local development)
    const buildTimeValue = viteEnv['VITE_GOOGLE_CLIENT_ID' as keyof ImportMetaEnv] as string | undefined;
    if (buildTimeValue !== undefined && buildTimeValue !== null && buildTimeValue !== '') {
      return buildTimeValue;
    }
    return '';
  },

  /**
   * Apple Sign In Services ID
   * Returns the configured Services ID or empty string if not configured.
   *
   * Priority:
   * 1. window.APP_CONFIG.APPLE_CLIENT_ID (runtime, Docker deployments)
   * 2. VITE_APPLE_CLIENT_ID (build-time, local development)
   * 3. '' (not configured)
   */
  get appleClientId(): string {
    // Check runtime config first (Docker deployments)
    const runtimeValue = window.APP_CONFIG?.APPLE_CLIENT_ID;
    if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
      return runtimeValue;
    }
    // Fall back to build-time value (local development)
    const buildTimeValue = viteEnv['VITE_APPLE_CLIENT_ID' as keyof ImportMetaEnv] as string | undefined;
    if (buildTimeValue !== undefined && buildTimeValue !== null && buildTimeValue !== '') {
      return buildTimeValue;
    }
    return '';
  },

  /**
   * Apple Sign In Redirect URI
   * Returns the configured redirect URI or current origin as fallback.
   *
   * Priority:
   * 1. window.APP_CONFIG.APPLE_REDIRECT_URI (runtime, Docker deployments)
   * 2. VITE_APPLE_REDIRECT_URI (build-time, local development)
   * 3. window.location.origin (default for popup mode)
   */
  get appleRedirectUri(): string {
    // Check runtime config first (Docker deployments)
    const runtimeValue = window.APP_CONFIG?.APPLE_REDIRECT_URI;
    if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
      return runtimeValue;
    }
    // Fall back to build-time value (local development)
    const buildTimeValue = viteEnv['VITE_APPLE_REDIRECT_URI' as keyof ImportMetaEnv] as string | undefined;
    if (buildTimeValue !== undefined && buildTimeValue !== null && buildTimeValue !== '') {
      return buildTimeValue;
    }
    // Default to current origin for popup mode
    return typeof window !== 'undefined' ? window.location.origin : '';
  },

  /**
   * Plaid environment (sandbox, development, production)
   */
  plaidEnv:
    window.APP_CONFIG?.PLAID_ENV ||
    import.meta.env.VITE_PLAID_ENV ||
    'sandbox',

  /**
   * Current environment (development, staging, production)
   */
  environment:
    window.APP_CONFIG?.ENVIRONMENT ||
    import.meta.env.VITE_APP_ENV ||
    'development',

  /**
   * Cloudflare Turnstile site key for captcha
   * Note: This is generally environment-specific but can be overridden at runtime
   */
  turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '',

  /**
   * Sentry DSN for error tracking
   * Note: Generally same across environments but can be overridden
   */
  sentryDsn: import.meta.env.VITE_SENTRY_DSN || '',

  /**
   * PostHog API key for analytics (WP-LANDING-2)
   * Returns empty string if not configured (analytics disabled).
   *
   * Priority:
   * 1. window.APP_CONFIG.POSTHOG_KEY (runtime, Docker deployments)
   * 2. VITE_POSTHOG_KEY (build-time, local development)
   * 3. '' (analytics disabled)
   */
  get posthogKey(): string {
    // Check runtime config first (Docker deployments)
    const runtimeValue = window.APP_CONFIG?.POSTHOG_KEY;
    if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
      return runtimeValue;
    }
    // Fall back to build-time value (local development)
    const buildTimeValue = viteEnv['VITE_POSTHOG_KEY' as keyof ImportMetaEnv] as string | undefined;
    if (buildTimeValue !== undefined && buildTimeValue !== null && buildTimeValue !== '') {
      return buildTimeValue;
    }
    return '';
  },

  /**
   * PostHog host URL for analytics (WP-LANDING-2)
   * Returns default PostHog cloud URL if not configured.
   *
   * Priority:
   * 1. window.APP_CONFIG.POSTHOG_HOST (runtime, Docker deployments)
   * 2. VITE_POSTHOG_HOST (build-time, local development)
   * 3. 'https://app.posthog.com' (default PostHog cloud)
   */
  get posthogHost(): string {
    // Check runtime config first (Docker deployments)
    const runtimeValue = window.APP_CONFIG?.POSTHOG_HOST;
    if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
      return runtimeValue;
    }
    // Fall back to build-time value (local development)
    const buildTimeValue = viteEnv['VITE_POSTHOG_HOST' as keyof ImportMetaEnv] as string | undefined;
    if (buildTimeValue !== undefined && buildTimeValue !== null && buildTimeValue !== '') {
      return buildTimeValue;
    }
    return 'https://app.posthog.com';
  },
} as const;

/**
 * Check if running in production environment
 */
export const isProduction = config.environment === 'production';

/**
 * Check if running in staging environment
 */
export const isStaging = config.environment === 'staging';

/**
 * Check if running in development environment
 */
export const isDevelopment = config.environment === 'development';
